/**
 * The edge fleet: every hospital node that has ever enrolled, as the cloud
 * last heard from it — and the one admin action, minting enrolment tokens.
 *
 * Two display rules carried over from the rest of the panel:
 *
 * A minted token is shown once, in the same shown-once styling as a rotated
 * password, because it is the same kind of object: the server stores a hash,
 * and this render is the only copy that will ever exist.
 *
 * The list's failure states are never conflated. "No nodes enrolled" (empty,
 * no error), "the migration has not run", "the database is not configured"
 * and "unreachable" each say so — an empty table that could mean any of four
 * things tells an operator nothing.
 *
 * ── the redesign ────────────────────────────────────────────────────────────
 *
 * Carbon is gone; the table, toolbar and tiles are the hand-built primitives.
 * Four things changed beyond the skin, each for a reason the old layout could
 * not express:
 *
 *   `status` and `liveness` are now two columns rather than two badges sharing
 *   one. They are orthogonal and they fail differently: `status` is a setting
 *   an operator chose (a disabled node's heartbeats are rejected at the
 *   ingest), `liveness` is derived from `last_seen_at` and nobody chose it. A
 *   node can be enabled and offline — the interesting case — and the old
 *   single-column reading hid exactly that combination.
 *
 *   The four registry states are now told apart by their *heading*, not by
 *   reading the server's sentence. `list_edge_nodes` returns one of three
 *   distinct strings, so the page classifies them into "not configured",
 *   "migration has not run" and "the query failed", each with its own tone and
 *   its own next step — and still prints the controller's own words underneath
 *   as the evidence. The fourth state, an empty list with an empty error, keeps
 *   its own separate empty block.
 *
 *   Sorting defaults to liveness worst-first, and Orthanc likewise, because a
 *   fleet console is read top-down during an incident. Nulls still sort last in
 *   both directions: a node that reported nothing is not a node reporting zero.
 *
 *   Freshness reads `fetchedAt`, not `new Date()`. The old header restamped
 *   itself on every render, so a page whose polling had silently died still
 *   said "just now" — the single most dangerous thing this header could claim.
 *
 * ── addresses ───────────────────────────────────────────────────────────────
 *
 * The "Addresses" column (LAN, public, mesh) is the LAST column on purpose:
 * `page-edge.css` hides columns by position, and appending keeps every one of
 * its `nth-child` numbers pointing where it did. It is left out entirely when
 * every row came back `addresses_hidden` — a viewer's table would otherwise
 * carry a full column of "Hidden". The side panel keeps the explicit
 * "Hidden — operator role" row, so a viewer can still tell withheld from
 * unreported. The display rules are in `components/EdgeAddresses`.
 *
 * The enrolment flows are unchanged in behaviour: the same full wizard, the
 * same token-only mint, the same NetBird setup key, all still shown once with
 * the same warning. They only moved into cards, and gained a copy button and
 * NetBird's own `terminal_enabled` / `ssh_user` facts, which the response
 * already carried and the page was throwing away.
 *
 * ── the compaction pass ─────────────────────────────────────────────────────
 *
 * Nothing below changes a route, a request, a guard or a state. What changed is
 * how much of the fleet fits on one screen:
 *
 *   The header's descriptive subtitle is gone. An operator who opens this page
 *   daily does not need to be told what an edge node is; the registry's failure
 *   callouts stay, because those are honesty, not prose.
 *
 *   The table keeps all eleven columns and all eleven search keys, but the three
 *   an incident never starts from — hospital, disk free, agent version — are
 *   hidden by `page-edge.css` below 80rem, and the merely useful ones below
 *   64rem, leaving node / center / liveness / Orthanc / unreplicated. Hidden by
 *   CSS rather than by dropping columns on purpose: a dropped column would also
 *   drop out of `DataTable`'s search, so typing an agent version at 1024px would
 *   silently stop matching. Every hidden fact is in the DetailPanel.
 *
 *   "Add a node" lost its explanatory card and became one bar: heading, the
 *   two-way choice, one line of consequence. The mint and NetBird panels sit
 *   side by side on the token path rather than stacked, and `NetbirdSetupKeyPanel`
 *   moved inside `AddNodeSection` to make that layout possible — it is still
 *   mounted unconditionally, so its status request is unchanged.
 *
 *   The page's own `CopyButton` is gone in favour of `components/ui/CopyButton`,
 *   which already announces a failed clipboard write in the label itself. The
 *   loud "Could not copy" note beside it was the only thing the local copy had
 *   that the shared one does not, and a label that reads "Copy failed" says the
 *   same thing in a quarter of the space.
 *
 *   The wizard is a shared component and is not edited. It is wrapped in
 *   `.edge-wizard` and compacted entirely by descendant CSS: its ~70 fields flow
 *   three-up instead of one-up, and its cards lost their loose gaps.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Badge, TriStateBadge, type Tone } from '../components/Badge';
import { Callout } from '../components/Callout';
import {
  ADDRESSES_HIDDEN_TEXT,
  AddressesCell,
  CONFLICT_TEXT,
  CONTAINER_TEXT,
  MeshSourceBadge,
  addressSearchText,
} from '../components/EdgeAddresses';
import { EdgeNodeWizard } from '../components/EdgeNodeWizard';
import { Freshness } from '../components/Freshness';
import { PageHeader } from '../components/PageHeader';
import { StatGrid, StatTile } from '../components/StatTile';
import { EmptyBlock, ErrorBlock, SkeletonRows, StaleBanner } from '../components/States';
import { Button } from '../components/ui/Button';
import { Card, CardBody, CardHeader, KeyValue, KeyValueRow } from '../components/ui/Card';
import { CopyButton } from '../components/ui/CopyButton';
import { DataTable, type Column } from '../components/ui/DataTable';
import { DetailPanel } from '../components/ui/DetailPanel';
import { FilterBar, SegmentedFilter } from '../components/ui/FilterBar';
import { Checkbox, NumberInput, Select, SelectItem, TextInput } from '../components/ui/Form';
import { AddIcon, ChevronRightIcon } from '../components/ui/Icon';
import { apiPost, describeError } from '../lib/api';
import {
  formatBytes,
  formatDateTime,
  formatDuration,
  formatNumber,
  formatRelative,
  parseDate,
  pluralise,
  UNKNOWN,
} from '../lib/format';
import { useResource } from '../lib/useResource';
import type {
  CenterListResponse,
  EdgeNodeListResponse,
  EdgeNodeResponse,
  MintTokenResponse,
  NetbirdStatusResponse,
  SetupKeyResponse,
} from '../types/api';

import '../styles/page-edge.css';

const REFRESH_MS = 30_000;

type Liveness = EdgeNodeResponse['liveness'];
type LivenessFilter = 'all' | Liveness;

const LIVENESS_TONE: Record<Liveness, Tone> = {
  online: 'good',
  stale: 'warning',
  offline: 'critical',
  never: 'neutral',
};

const LIVENESS_LABEL: Record<Liveness, string> = {
  online: 'Online',
  stale: 'Stale',
  offline: 'Offline',
  never: 'Never seen',
};

/** What the word actually means, on hover — the server derives it, not us. */
const LIVENESS_HINT: Record<Liveness, string> = {
  online: 'Heartbeating within the expected window.',
  stale: 'Heartbeats arrived once, but not recently enough.',
  offline: 'Long enough without a heartbeat to be treated as down.',
  never: 'Enrolled, but this node has never checked in.',
};

/**
 * The sort key for liveness, which doubles as the search key.
 *
 * The leading digit orders the column by how much it wants a human — offline,
 * stale, never seen, then online — so the default sort puts the fleet's
 * problems at the top. The word after it is what keeps typing "offline" into
 * the search box working, as it did before the migration.
 */
const LIVENESS_SORT: Record<Liveness, string> = {
  offline: '0 offline',
  stale: '1 stale',
  never: '2 never seen',
  online: '3 online',
};

/** Free bytes as a share of the disk, or null when either end is unreported. */
function diskShare(node: EdgeNodeResponse): number | null {
  const { disk_free_bytes: free, disk_total_bytes: total } = node;
  if (free === null || total === null || total <= 0) return null;
  return Math.round((free / total) * 100);
}

/* ── the registry's failure states ─────────────────────────────────────────── */

interface RegistryFault {
  tone: Tone;
  title: string;
  detail: ReactNode;
}

/**
 * `EdgeNodeListResponse.error` is prose from the controller, but only three
 * sentences are ever produced (`db.list_edge_nodes`). Classifying them is what
 * turns "something is wrong" into three different next moves — and the raw
 * string is still shown underneath, because the classification is a convenience
 * and the server's own words are the evidence.
 */
function classifyRegistryFault(message: string): RegistryFault {
  const text = message.toLowerCase();

  if (text.includes('not configured')) {
    return {
      tone: 'info',
      title: 'The edge registry is not configured',
      detail: (
        <>
          The controller has no clinic database connection, so it has nowhere to read
          enrolled nodes from. Nothing can be concluded about the fleet from this page
          until <code>FLEET_CLINIC_DSN</code> is set — in particular, this is{' '}
          <strong>not</strong> evidence that no nodes exist.
        </>
      ),
    };
  }

  // The table exists but predates a column migration (023 or 024): nodes may
  // well be enrolled, the controller just cannot read the columns it needs yet.
  // The server's sentence, printed under the callout, names the migration(s).
  if (text.includes('predates migration')) {
    return {
      tone: 'warning',
      title: 'The registry needs a schema update',
      detail: (
        <>
          The clinic database has the edge_nodes table, but not the columns this
          controller reads. Enrolled nodes may exist; they cannot be listed or opened
          here until the migration(s) named below are applied.
        </>
      ),
    };
  }

  if (text.includes('do not exist') || text.includes('migration')) {
    return {
      tone: 'warning',
      title: 'The registry tables have not been created yet',
      detail: (
        <>
          The clinic database answers, but the edge schema is missing, so there is no
          table to list. Run the migration named below. Nodes already in the field are
          unaffected; they simply have nowhere to register.
        </>
      ),
    };
  }

  return {
    tone: 'critical',
    title: 'The registry could not be read',
    detail: (
      <>
        The clinic database is configured but the query failed, so this page cannot say
        whether any node is enrolled. Nodes keep heartbeating regardless — this is a
        controller-side failure, and the controller&rsquo;s logs will have the traceback.
      </>
    ),
  };
}

/* ── the fleet table ───────────────────────────────────────────────────────── */

export function EdgeNodesPage() {
  const nodes = useResource<EdgeNodeListResponse>('/api/edge-nodes', {
    refreshMs: REFRESH_MS,
  });

  const [search, setSearch] = useState('');
  const [liveness, setLiveness] = useState<LivenessFilter>('all');
  const [openUuid, setOpenUuid] = useState<string | null>(null);

  const data = nodes.data;
  const rows = useMemo(() => data?.nodes ?? [], [data]);
  const fault = data?.error ? classifyRegistryFault(data.error) : null;

  const counts = useMemo(() => {
    const tally = { all: rows.length, online: 0, stale: 0, offline: 0, never: 0, disabled: 0 };
    for (const node of rows) {
      tally[node.liveness] += 1;
      if (node.status === 'disabled') tally.disabled += 1;
    }
    return tally;
  }, [rows]);

  const orthanc = useMemo(() => {
    let down = 0;
    let unreported = 0;
    for (const node of rows) {
      if (node.orthanc_up === false) down += 1;
      else if (node.orthanc_up === null) unreported += 1;
    }
    return { down, unreported };
  }, [rows]);

  /**
   * The replication backlog, summed only over the nodes that reported one.
   * A node that reported nothing contributes nothing and is counted separately
   * — folding its silence in as a zero would understate the queue.
   */
  const backlog = useMemo(() => {
    let total = 0;
    let reporting = 0;
    let behind = 0;
    for (const node of rows) {
      if (node.unreplicated_count === null) continue;
      reporting += 1;
      total += node.unreplicated_count;
      if (node.unreplicated_count > 0) behind += 1;
    }
    return { total, reporting, behind, silent: rows.length - reporting };
  }, [rows]);

  const filtered = useMemo(
    () => (liveness === 'all' ? rows : rows.filter((node) => node.liveness === liveness)),
    [rows, liveness],
  );

  const open = openUuid ? rows.find((node) => node.node_uuid === openUuid) : undefined;
  // Every row withheld means the caller is below operator: no column at all.
  const showAddresses = rows.length > 0 && rows.some((node) => !node.addresses_hidden);

  const columns: ReadonlyArray<Column<EdgeNodeResponse, string>> = useMemo(
    () => [
      {
        key: 'node',
        header: 'Node',
        // Sorted by the name an operator reads, searchable by the uuid they
        // were given in a ticket.
        value: (node) => `${node.label ?? node.node_uuid} ${node.node_uuid}`,
        render: (node) => (
          <div className="edge-node">
            <Link
              to={`/edge-nodes/${encodeURIComponent(node.node_uuid)}`}
              className="edge-node__name"
            >
              {node.label ?? node.node_uuid}
            </Link>
            <span className="edge-node__id" title={node.node_uuid}>
              {node.node_uuid}
            </span>
          </div>
        ),
      },
      {
        key: 'hospital',
        header: 'Hospital',
        value: (node) => node.hospital ?? null,
        render: (node) =>
          node.hospital ? node.hospital : <span className="muted">{UNKNOWN}</span>,
      },
      {
        key: 'center',
        header: 'Center',
        value: (node) =>
          node.center_slug ?? (node.doctor_id !== null ? `doctor ${node.doctor_id}` : null),
        render: (node) =>
          node.center_slug ? (
            <Link to={`/centers/${node.center_slug}`}>{node.center_slug}</Link>
          ) : node.doctor_id !== null ? (
            // A real, resolved linkage that simply has no center row here.
            <span className="muted">doctor {node.doctor_id} (no center)</span>
          ) : (
            <Badge tone="warning" small title="This node's heartbeats cannot be attributed to a center.">
              Unlinked
            </Badge>
          ),
      },
      {
        key: 'liveness',
        header: 'Liveness',
        value: (node) => LIVENESS_SORT[node.liveness],
        render: (node) => (
          <Badge tone={LIVENESS_TONE[node.liveness]} small title={LIVENESS_HINT[node.liveness]}>
            {LIVENESS_LABEL[node.liveness]}
          </Badge>
        ),
      },
      {
        key: 'status',
        header: 'Enrolment',
        value: (node) => node.status,
        render: (node) =>
          node.status === 'disabled' ? (
            <Badge
              tone="neutral"
              small
              title="Set by an operator. While it is disabled, this node's heartbeats are rejected at the ingest."
            >
              Disabled
            </Badge>
          ) : (
            // Enabled is the norm; the norm gets no ink.
            <span className="muted">Enabled</span>
          ),
      },
      {
        key: 'orthanc',
        header: 'Orthanc',
        // Sorts "down" before "up" before nulls, and keeps the version text
        // reachable from the search box.
        value: (node) =>
          node.orthanc_up === null
            ? null
            : `${node.orthanc_up ? 'up' : 'down'} ${node.orthanc_version ?? ''}`,
        render: (node) => (
          <div className="edge-cell">
            <TriStateBadge
              value={node.orthanc_up}
              trueLabel="Up"
              falseLabel="Down"
              nullLabel="Not reported"
            />
            {node.orthanc_version ? (
              <span className="edge-cell__note mono">{node.orthanc_version}</span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'studies',
        header: 'Studies',
        numeric: true,
        value: (node) => node.studies_count,
        render: (node) => formatNumber(node.studies_count),
      },
      {
        key: 'unreplicated',
        header: 'Unreplicated',
        numeric: true,
        value: (node) => node.unreplicated_count,
        render: (node) =>
          node.unreplicated_count === null ? (
            <span className="muted">{UNKNOWN}</span>
          ) : node.unreplicated_count > 0 ? (
            <strong
              className="edge-backlog"
              title="Studies this node holds that the cloud has not acknowledged."
            >
              {formatNumber(node.unreplicated_count)}
            </strong>
          ) : (
            <span className="edge-zero">0</span>
          ),
      },
      {
        key: 'disk_free',
        header: 'Disk free',
        numeric: true,
        value: (node) => node.disk_free_bytes,
        render: (node) => {
          const share = diskShare(node);
          return (
            <div className="edge-cell edge-cell--num">
              <span>{formatBytes(node.disk_free_bytes)}</span>
              {share !== null ? (
                <span className="edge-cell__note">
                  {share}% of {formatBytes(node.disk_total_bytes)}
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        key: 'agent',
        header: 'Agent',
        value: (node) => node.agent_version ?? null,
        render: (node) =>
          node.agent_version ? (
            <span className="mono">{node.agent_version}</span>
          ) : (
            <span className="muted">{UNKNOWN}</span>
          ),
      },
      {
        key: 'last_seen',
        header: 'Last seen',
        value: (node) => node.last_seen_at,
        render: (node) => (
          <span className="muted" title={formatDateTime(node.last_seen_at)}>
            {formatRelative(node.last_seen_at)}
          </span>
        ),
      },
      // Last, so page-edge.css's positional hiding is undisturbed (see top).
      ...(showAddresses
        ? [
            {
              key: 'addresses',
              header: 'Addresses',
              value: addressSearchText,
              render: (node: EdgeNodeResponse) => <AddressesCell node={node} />,
            },
          ]
        : []),
    ],
    [showAddresses],
  );

  const notReporting = counts.offline + counts.stale + counts.never;
  const stale = Boolean(nodes.error && data);

  return (
    <>
      <PageHeader
        title="Edge nodes"
        status={
          data && rows.length > 0 ? (
            <Badge
              tone={counts.offline > 0 ? 'critical' : notReporting > 0 ? 'warning' : 'good'}
            >
              {counts.online}/{counts.all} online
            </Badge>
          ) : null
        }
        actions={
          <>
            <Freshness
              checkedAt={nodes.fetchedAt}
              onRefresh={nodes.reload}
              refreshing={nodes.refreshing}
              staleAfterMs={REFRESH_MS * 3}
            />
            {/* A jump to the enrolment section further down the same page, so a
                real anchor: it is focusable, it announces itself as a link, and
                the browser's own fragment handling does the scrolling. */}
            <a href="#add-node" className="btn btn--secondary btn--sm">
              <AddIcon size={14} className="btn__icon" />
              Add a node
            </a>
          </>
        }
      />

      {stale ? <StaleBanner error={nodes.error} onRetry={nodes.reload} /> : null}

      {nodes.loading && !data ? (
        <SkeletonRows rows={5} />
      ) : nodes.error && !data ? (
        <ErrorBlock what="edge nodes" error={nodes.error} onRetry={nodes.reload} />
      ) : data ? (
        <>
          {fault ? (
            <Callout tone={fault.tone} title={fault.title} live>
              {fault.detail}
              <p className="edge-fault__raw">{data.error}</p>
            </Callout>
          ) : null}

          {rows.length === 0 && !fault ? (
            <EmptyBlock
              title="No nodes enrolled"
              action={
                <a href="#add-node" className="btn btn--primary btn--md">
                  Enrol the first node
                </a>
              }
            >
              The registry is reachable, the schema is present, and it is genuinely
              empty. This is the one empty state that means what it looks like.
            </EmptyBlock>
          ) : rows.length > 0 ? (
            <>
              <StatGrid>
                <StatTile
                  label="Enrolled"
                  value={formatNumber(counts.all)}
                  hint={
                    counts.disabled > 0
                      ? `${formatNumber(counts.disabled)} disabled by an operator`
                      : 'none disabled'
                  }
                />
                <StatTile
                  label="Online"
                  value={formatNumber(counts.online)}
                  hint={`of ${formatNumber(counts.all)} ${pluralise(counts.all, 'node')}`}
                />
                <StatTile
                  label="Not reporting"
                  value={formatNumber(notReporting)}
                  tone={
                    counts.offline > 0 ? 'critical' : notReporting > 0 ? 'warning' : 'neutral'
                  }
                  hint={
                    notReporting === 0
                      ? 'every node has checked in'
                      : [
                          counts.offline > 0 ? `${counts.offline} offline` : '',
                          counts.stale > 0 ? `${counts.stale} stale` : '',
                          counts.never > 0 ? `${counts.never} never seen` : '',
                        ]
                          .filter(Boolean)
                          .join(' · ')
                  }
                />
                <StatTile
                  label="Orthanc down"
                  value={formatNumber(orthanc.down)}
                  tone={orthanc.down > 0 ? 'critical' : 'neutral'}
                  hint={
                    orthanc.unreported > 0
                      ? `${formatNumber(orthanc.unreported)} not reported`
                      : 'every node reported'
                  }
                />
                <StatTile
                  label="Unreplicated studies"
                  // Nothing reported means nothing known, which is not zero.
                  value={backlog.reporting === 0 ? UNKNOWN : formatNumber(backlog.total)}
                  tone={backlog.total > 0 ? 'warning' : 'neutral'}
                  hint={
                    backlog.reporting === 0
                      ? 'no node reported a queue depth'
                      : backlog.silent > 0
                        ? `${formatNumber(backlog.behind)} behind · ${formatNumber(backlog.silent)} not reporting`
                        : backlog.behind > 0
                          ? `on ${formatNumber(backlog.behind)} ${pluralise(backlog.behind, 'node')}`
                          : 'every node is caught up'
                  }
                />
              </StatGrid>

              <FilterBar
                search={search}
                onSearch={setSearch}
                searchPlaceholder="Search node, uuid, hospital, center, agent…"
                searchLabel="Search edge nodes"
                count={
                  filtered.length === rows.length
                    ? `${formatNumber(rows.length)} ${pluralise(rows.length, 'node')}`
                    : `${formatNumber(filtered.length)} of ${formatNumber(rows.length)}`
                }
              >
                <SegmentedFilter<LivenessFilter>
                  legend="Filter by liveness"
                  value={liveness}
                  onChange={setLiveness}
                  options={[
                    { value: 'all', label: 'All', count: counts.all },
                    { value: 'online', label: 'Online', count: counts.online },
                    { value: 'stale', label: 'Stale', count: counts.stale },
                    { value: 'offline', label: 'Offline', count: counts.offline },
                    { value: 'never', label: 'Never seen', count: counts.never },
                  ]}
                />
              </FilterBar>

              {/* The wrapper is the hook page-edge.css uses to drop columns by
                  position at narrow widths. The columns themselves stay in the
                  array, so every one of them is still searchable. */}
              <Card flush className="edge-table">
                <DataTable<EdgeNodeResponse, string>
                  rows={filtered}
                  columns={columns}
                  getRowId={(node) => node.node_uuid}
                  label="Edge nodes"
                  initialSortKey="liveness"
                  search={search}
                  onRowClick={(node) => setOpenUuid(node.node_uuid)}
                  isRowActive={(node) => node.node_uuid === openUuid}
                  emptyFiltered={
                    <EmptyBlock title="Nothing matches those filters">
                      {search ? (
                        <>
                          No node matches <strong>{search}</strong>
                          {liveness !== 'all' ? ' with that liveness' : ''}.
                        </>
                      ) : (
                        'No node has that liveness right now.'
                      )}
                    </EmptyBlock>
                  }
                />
              </Card>
            </>
          ) : null}
        </>
      ) : null}

      <AddNodeSection onChanged={nodes.reload} />

      <DetailPanel
        open={open !== undefined}
        title={open ? (open.label ?? open.node_uuid) : ''}
        subtitle={open?.hospital ?? open?.node_uuid}
        onClose={() => setOpenUuid(null)}
        footer={
          open ? (
            <Link
              to={`/edge-nodes/${encodeURIComponent(open.node_uuid)}`}
              className="btn btn--primary btn--md"
            >
              Open full node page
              <ChevronRightIcon size={14} />
            </Link>
          ) : null
        }
      >
        {open ? <NodePanelBody node={open} /> : null}
      </DetailPanel>
    </>
  );
}

/** Everything the registry holds about one node, in the order it is asked for. */
function NodePanelBody({ node }: { node: EdgeNodeResponse }) {
  const share = diskShare(node);
  const behind = node.unreplicated_count !== null && node.unreplicated_count > 0;

  return (
    <>
      <section className="stack">
        <h3 className="section__subtitle">State</h3>
        <div className="row">
          <Badge tone={LIVENESS_TONE[node.liveness]}>{LIVENESS_LABEL[node.liveness]}</Badge>
          <TriStateBadge
            value={node.orthanc_up}
            trueLabel="Orthanc up"
            falseLabel="Orthanc down"
            nullLabel="Orthanc not reported"
          />
          {node.status === 'disabled' ? <Badge tone="neutral">Disabled</Badge> : null}
        </div>
        <p className="muted">{LIVENESS_HINT[node.liveness]}</p>
        {node.status === 'disabled' ? (
          <Callout tone="info" title="This node is disabled">
            An operator set this. Its heartbeats are rejected at the ingest, so its
            telemetry below will stop advancing until it is enabled again — the node
            itself has not been touched.
          </Callout>
        ) : null}
      </section>

      <section className="stack">
        <h3 className="section__subtitle">Replication</h3>
        {behind ? (
          <Callout tone="warning" title={`${formatNumber(node.unreplicated_count)} studies not yet in the cloud`}>
            These exist only at the hospital until the agent uploads them.
          </Callout>
        ) : null}
        <KeyValue>
          <KeyValueRow label="Studies">{formatNumber(node.studies_count)}</KeyValueRow>
          <KeyValueRow label="Instances">{formatNumber(node.instances_count)}</KeyValueRow>
          <KeyValueRow label="Unreplicated">
            {node.unreplicated_count === null ? (
              <span className="muted">{UNKNOWN}</span>
            ) : node.unreplicated_count > 0 ? (
              <strong className="edge-backlog">{formatNumber(node.unreplicated_count)}</strong>
            ) : (
              <span className="edge-zero">0</span>
            )}
          </KeyValueRow>
          <KeyValueRow label="Last replication">
            <span title={formatDateTime(node.last_replication_success_at)}>
              {formatRelative(node.last_replication_success_at)}
            </span>
          </KeyValueRow>
          <KeyValueRow label="Last seen">
            <span title={formatDateTime(node.last_seen_at)}>
              {formatRelative(node.last_seen_at)}
            </span>
          </KeyValueRow>
        </KeyValue>
      </section>

      <section className="stack">
        <h3 className="section__subtitle">Storage</h3>
        <KeyValue>
          <KeyValueRow label="Free">
            {formatBytes(node.disk_free_bytes)}
            {share !== null ? <span className="muted"> ({share}%)</span> : null}
          </KeyValueRow>
          <KeyValueRow label="Total">{formatBytes(node.disk_total_bytes)}</KeyValueRow>
        </KeyValue>
      </section>

      <section className="stack">
        <h3 className="section__subtitle">Addresses</h3>
        <NodeAddresses node={node} />
      </section>

      <section className="stack">
        <h3 className="section__subtitle">Identity</h3>
        <KeyValue>
          <KeyValueRow label="Node uuid">
            <span className="mono">{node.node_uuid}</span>
          </KeyValueRow>
          <KeyValueRow label="Label">
            {node.label ?? <span className="muted">{UNKNOWN}</span>}
          </KeyValueRow>
          <KeyValueRow label="Hospital">
            {node.hospital ?? <span className="muted">{UNKNOWN}</span>}
          </KeyValueRow>
          <KeyValueRow label="Center">
            {node.center_slug ? (
              <Link to={`/centers/${node.center_slug}`}>{node.center_slug}</Link>
            ) : (
              <span className="muted">Not linked to a center</span>
            )}
          </KeyValueRow>
          <KeyValueRow label="Doctor id">
            {node.doctor_id !== null ? (
              <span className="mono">{node.doctor_id}</span>
            ) : (
              <span className="muted">{UNKNOWN}</span>
            )}
          </KeyValueRow>
          <KeyValueRow label="Cloud peer">
            {node.cloud_peer_host ? (
              <span className="mono">{node.cloud_peer_host}</span>
            ) : (
              <span className="muted">{UNKNOWN}</span>
            )}
          </KeyValueRow>
          {/* Host facts. Every one is null on a node whose agent predates
              migration 023, and "Not reported" is the honest rendering of that
              — an unreported uptime is not an uptime of zero. */}
          <KeyValueRow label="Uptime">
            {node.uptime_s !== null ? (
              formatDuration(node.uptime_s)
            ) : (
              <span className="muted">Not reported by this agent</span>
            )}
          </KeyValueRow>
          <KeyValueRow label="Auto-update">
            <TriStateBadge
              value={node.auto_update}
              trueLabel="Enabled"
              falseLabel="Disabled"
              nullLabel="Not reported"
              trueTone="good"
              falseTone="neutral"
            />
          </KeyValueRow>
          <KeyValueRow label="Agent">
            {node.agent_version ? (
              <span className="mono">{node.agent_version}</span>
            ) : (
              <span className="muted">{UNKNOWN}</span>
            )}
          </KeyValueRow>
          <KeyValueRow label="Orthanc">
            {node.orthanc_version ? (
              <span className="mono">{node.orthanc_version}</span>
            ) : (
              <span className="muted">{UNKNOWN}</span>
            )}
          </KeyValueRow>
          <KeyValueRow label="Config hash">
            {node.config_hash ? (
              <span className="mono edge-node__id" title={node.config_hash}>
                {node.config_hash}
              </span>
            ) : (
              <span className="muted">{UNKNOWN}</span>
            )}
          </KeyValueRow>
          <KeyValueRow label="Enrolled">
            <span title={formatDateTime(node.created_at)}>
              {formatRelative(node.created_at)}
            </span>
          </KeyValueRow>
        </KeyValue>
      </section>
    </>
  );
}

/**
 * LAN, public and mesh addresses, the NetBird peer and the host key
 * fingerprint — or one "Hidden" row for a viewer, since the server withheld
 * all of them and "Not reported" on each would claim the node is silent.
 */
function NodeAddresses({ node }: { node: EdgeNodeResponse }) {
  if (node.addresses_hidden) {
    return (
      <KeyValue>
        <KeyValueRow label="Addresses">
          <span className="muted">{ADDRESSES_HIDDEN_TEXT}</span>
        </KeyValueRow>
      </KeyValue>
    );
  }
  return (
    <>
      {node.mesh_ip_conflict ? (
        <Callout tone="warning" title="Mesh address conflict">
          {CONFLICT_TEXT}
        </Callout>
      ) : null}
      <KeyValue>
        <KeyValueRow label="LAN address">
          {node.private_ip ? (
            <>
              <span className="mono" title="Self-reported by the node — a diagnostic, not an identity.">
                {node.private_ip}
              </span>
              {node.private_ip_is_container ? (
                <>
                  {' '}
                  <Badge tone="warning" small title={CONTAINER_TEXT}>
                    container address
                  </Badge>
                </>
              ) : null}
            </>
          ) : (
            <span className="muted">Not reported</span>
          )}
        </KeyValueRow>
        <KeyValueRow label="Public address">
          {node.public_ip ? (
            <span className="mono" title="Observed by the ingest from the connection, never self-reported.">
              {node.public_ip}
            </span>
          ) : (
            <span className="muted">Not observed</span>
          )}
        </KeyValueRow>
        <KeyValueRow label="Mesh (NetBird) address">
          {node.mesh_ip ? (
            <>
              <span className="mono">{node.mesh_ip}</span>{' '}
              <MeshSourceBadge source={node.mesh_ip_source} />
            </>
          ) : node.netbird_peer_id ? (
            <span className="muted">Linked to a peer, which has no address yet</span>
          ) : (
            <span className="muted">Not reported, and not linked to a peer</span>
          )}
        </KeyValueRow>
        <KeyValueRow label="NetBird peer">
          {node.netbird_peer_id ? (
            <span className="mono">{node.netbird_peer_id}</span>
          ) : (
            <span className="muted">Not linked</span>
          )}
        </KeyValueRow>
        <KeyValueRow label="SSH host key">
          {node.ssh_host_key_fingerprint ? (
            <span
              className="mono edge-node__id"
              title="SHA-256 fingerprint of the SSH host key the node's agent reported. The key itself never leaves the server."
            >
              {node.ssh_host_key_fingerprint}
            </span>
          ) : (
            <span className="muted">Not reported by this agent</span>
          )}
        </KeyValueRow>
      </KeyValue>
    </>
  );
}

/* ── enrolment ─────────────────────────────────────────────────────────────── */

/**
 * The two ways to add a node, as one choice rather than two competing panels.
 *
 * Full setup is the default because it is the one that leaves nothing to be
 * hand-edited on the node. Token only remains for the case the wizard cannot
 * serve: a node whose config already exists and just needs to re-enrol.
 *
 * The choice is a radio group rather than a select: there are exactly two
 * options, both are worth reading, and one of them is the default — which is a
 * segmented control's whole shape.
 *
 * Compacted: the chooser is a bar rather than a card with a paragraph in it, and
 * the NetBird panel lives here so the token path can put the two mint panels
 * side by side. It is rendered unconditionally, exactly as it was when it sat a
 * level up, so its status request still fires on every visit.
 */
function AddNodeSection({ onChanged }: { onChanged: () => void }) {
  const [mode, setMode] = useState<'full' | 'token'>('full');

  return (
    <div className="stack edge-add" id="add-node">
      <div className="edge-mode">
        <h2 className="section__title">Add a node</h2>
        <SegmentedFilter<'full' | 'token'>
          legend="How to add this node"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'full', label: 'Full setup' },
            { value: 'token', label: 'Token only' },
          ]}
        />
        {/* Not description: the consequence of the choice, which differs and is
            the thing an operator gets wrong. Owner role and the hash-only
            storage are stated on the panels that actually mint. */}
        <p className="edge-mode__note">
          {mode === 'full'
            ? 'Returns a token, a complete center-config.yaml with it baked in, and the install commands.'
            : 'Returns a token only — for a node that already has a center-config.yaml.'}
        </p>
      </div>

      <div className={mode === 'full' ? 'edge-enrol' : 'edge-enrol edge-enrol--split'}>
        {mode === 'full' ? (
          // Shared component: compacted from page-edge.css through this wrapper,
          // never by editing it.
          <div className="edge-wizard">
            <EdgeNodeWizard onProvisioned={onChanged} />
          </div>
        ) : (
          <MintPanel onMinted={onChanged} />
        )}
        <NetbirdSetupKeyPanel />
      </div>
    </div>
  );
}

function MintPanel({ onMinted }: { onMinted: () => void }) {
  const centers = useResource<CenterListResponse>('/api/centers');
  const [label, setLabel] = useState('');
  const [centerSlug, setCenterSlug] = useState('');
  const [expiresHours, setExpiresHours] = useState(24);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [minted, setMinted] = useState<MintTokenResponse | null>(null);

  async function mint() {
    setBusy(true);
    setError(null);
    try {
      setMinted(
        await apiPost<MintTokenResponse>('/api/edge-nodes/tokens', {
          label,
          center_slug: centerSlug || null,
          expires_hours: expiresHours,
        }),
      );
      setLabel('');
      onMinted();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card tone={minted ? 'warning' : undefined}>
      {/* The pre-assignment rule that used to be in this subtitle now sits on
          the field it governs, where it is read at the moment it applies. */}
      <CardHeader
        title="Token only"
        subtitle="Owner role. Single-use, stored server-side as a hash."
      />
      <CardBody>
        <div className="stack">
          {error ? (
            <Callout tone="critical" title="Could not mint" live>
              {error}
            </Callout>
          ) : null}

          {minted ? (
            <>
              <Callout tone="warning" title="Copy this now — it is shown once">
                The server keeps only a hash. If it is lost before the node enrols, mint
                another; this one expires unused.
              </Callout>
              <div className="edge-secret">
                <code className="secret">{minted.token}</code>
                <CopyButton text={minted.token} label="Copy token" kind="tertiary" />
              </div>
              <KeyValue>
                <KeyValueRow label="For">
                  {minted.label}
                  {minted.doctor_id !== null ? (
                    <span className="muted"> — pre-assigned to doctor {minted.doctor_id}</span>
                  ) : null}
                </KeyValueRow>
                <KeyValueRow label="Valid for">
                  {formatNumber(minted.expires_hours)}{' '}
                  {pluralise(minted.expires_hours, 'hour')}
                </KeyValueRow>
              </KeyValue>
              <p className="edge-help">{minted.usage}</p>
              <div className="row">
                <Button kind="tertiary" size="sm" onClick={() => setMinted(null)}>
                  Done — hide the token
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="edge-form">
                <TextInput
                  id="mint-label"
                  labelText="Label"
                  value={label}
                  onChange={(event) => setLabel(event.target.value)}
                  placeholder="City Hospital basement node"
                />
                <Select
                  id="mint-center"
                  labelText="Pre-assign center (optional)"
                  value={centerSlug}
                  onChange={(event) => setCenterSlug(event.target.value)}
                  helperText="Links the node to that center's doctor id. Without one, linkage is derived from the node's cloud peer hostname."
                >
                  <SelectItem value="" text="None — link from hostname" />
                  {(centers.data?.centers ?? []).map((center) => (
                    <SelectItem
                      key={center.slug}
                      value={center.slug}
                      text={`${center.slug} — ${center.domain}`}
                    />
                  ))}
                </Select>
                {/* A number between 1 and 336 does not need a full text column. */}
                <NumberInput
                  id="mint-expires"
                  label="Expires (hours)"
                  className="edge-field--num"
                  min={1}
                  max={336}
                  value={expiresHours}
                  onChange={(_event, { value }: { value: number | string }) =>
                    setExpiresHours(Math.max(1, Math.min(336, Number(value) || 24)))
                  }
                />
              </div>
              <div className="edge-form__actions">
                <Button size="sm" onClick={() => void mint()} disabled={!label.trim() || busy}>
                  {busy ? 'Minting…' : 'Mint token'}
                </Button>
                {centers.error ? (
                  <span className="muted">
                    The center list could not be loaded, so pre-assignment is unavailable;
                    minting without it still works.
                  </span>
                ) : null}
              </div>
            </>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * Mint a NetBird setup key — the credential a node needs to join the mesh, which
 * is what makes the in-browser terminal able to reach it. Shown once, like an
 * enrolment token: the plaintext exists in this render and NetBird's store, and
 * nowhere here. Owner-only on the server; the 403 explains itself.
 */
function NetbirdSetupKeyPanel() {
  const status = useResource<NetbirdStatusResponse>('/api/edge-nodes/netbird/status');
  const [name, setName] = useState('');
  const [reusable, setReusable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [minted, setMinted] = useState<SetupKeyResponse | null>(null);

  async function mint() {
    setBusy(true);
    setError(null);
    try {
      setMinted(
        await apiPost<SetupKeyResponse>('/api/edge-nodes/netbird/setup-keys', {
          name,
          reusable,
        }),
      );
      setName('');
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  const configured = status.data?.configured ?? false;

  return (
    <Card tone={minted ? 'warning' : undefined}>
      <CardHeader
        title="NetBird mesh access"
        subtitle="Owner role. A key is what lets a node join the mesh the terminal rides on."
      />
      <CardBody>
        <div className="stack">
          {status.data && !configured ? (
            <Callout tone="info" title="NetBird is not configured">
              Set <code>FLEET_NETBIRD_API_URL</code> and{' '}
              <code>FLEET_NETBIRD_API_TOKEN</code> on the controller to mint keys and
              reach nodes over the mesh.
            </Callout>
          ) : null}

          {status.error && !status.data ? (
            <ErrorBlock
              what="the NetBird status"
              error={status.error}
              onRetry={status.reload}
            />
          ) : null}

          {status.data && configured ? (
            <KeyValue>
              <KeyValueRow label="Terminal">
                {/* The kill switch is separate from being configured: keys and
                    peers work while it is off, the terminal does not. */}
                {status.data.terminal_enabled ? (
                  <Badge tone="good" small>
                    Enabled
                  </Badge>
                ) : (
                  <Badge tone="neutral" small title="Setup keys and peers still work; the in-browser terminal is switched off.">
                    Switched off
                  </Badge>
                )}
              </KeyValueRow>
              <KeyValueRow label="SSH user">
                {status.data.ssh_user ? (
                  <span className="mono">{status.data.ssh_user}</span>
                ) : (
                  <span className="muted">{UNKNOWN}</span>
                )}
              </KeyValueRow>
            </KeyValue>
          ) : null}

          {error ? (
            <Callout tone="critical" title="Could not mint" live>
              {error}
            </Callout>
          ) : null}

          {minted ? (
            <>
              <Callout tone="warning" title="Copy this now — it is shown once">
                NetBird keeps the key; this is the only time it is shown. If it is lost
                before the node joins, mint another.
              </Callout>
              <div className="edge-secret">
                <code className="secret">{minted.key}</code>
                <CopyButton text={minted.key} label="Copy setup key" kind="tertiary" />
              </div>
              <KeyValue>
                <KeyValueRow label="Name">{minted.name}</KeyValueRow>
                <KeyValueRow label="Type">
                  {minted.type} · {minted.state}
                </KeyValueRow>
                <KeyValueRow label="Expires">
                  {/* NetBird's own field. If it is not a timestamp we can parse,
                      show what it actually said rather than an em dash that
                      claims the value is unknown. */}
                  {parseDate(minted.expires) ? (
                    <span title={minted.expires}>{formatDateTime(minted.expires)}</span>
                  ) : minted.expires ? (
                    <span className="mono">{minted.expires}</span>
                  ) : (
                    <span className="muted">{UNKNOWN}</span>
                  )}
                </KeyValueRow>
              </KeyValue>
              <p className="edge-help">{minted.usage}</p>
              <div className="row">
                <Button kind="tertiary" size="sm" onClick={() => setMinted(null)}>
                  Done — hide the key
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="edge-form">
                <TextInput
                  id="setup-key-name"
                  labelText="Key name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="City Hospital node"
                  disabled={!configured}
                />
                <Checkbox
                  id="setup-key-reusable"
                  labelText="Reusable (multiple nodes)"
                  checked={reusable}
                  disabled={!configured}
                  helperText="A single-use key is consumed by the first node that joins with it."
                  onChange={(_event, { checked }: { checked: boolean }) => setReusable(checked)}
                />
              </div>
              <div className="edge-form__actions">
                <Button
                  size="sm"
                  onClick={() => void mint()}
                  disabled={!name.trim() || busy || !configured}
                >
                  {busy ? 'Minting…' : 'Mint setup key'}
                </Button>
              </div>
            </>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
